import { RetailerProviders } from "@faire/retailer-visitor-shared/hooks/retailer/RetailerProviders";
import { getRetailer } from "@faire/retailer-visitor-shared/serialized-data/getRetailer";
import fetchRetailer from "@faire/web-api--source/endpoints/www/api/retailer/retailerToken/get";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { createContext, useContext, useState } from "react";
import { createStore, useStore } from "zustand";
import { StoreApi } from "zustand";

type RetailerCore = {
  retailer: IRetailer | undefined;
  refetchRetailer: () => Promise<void>;
  setRetailer: (retailer: IRetailer | undefined) => void;
  retailerName: string | undefined;
  retailerToken: string | undefined;
};

const RetailerCoreContext = createContext<StoreApi<RetailerCore> | undefined>(
  undefined
);

export const RetailerCoreProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const initialRetailer = getRetailer();
  const [store] = useState(() =>
    createStore<RetailerCore>((set) => ({
      retailer: initialRetailer,
      setRetailer: (newRetailer: IRetailer | undefined) => {
        if (!newRetailer) {
          return;
        }
        set({
          retailer: newRetailer,
          retailerName: newRetailer.name,
          retailerToken: newRetailer.token,
        });
      },
      refetchRetailer: async () => {
        const retailer = await fetchRetailer("me");
        set({
          retailer,
          retailerName: retailer.name,
          retailerToken: retailer.token,
        });
      },
      retailerName: initialRetailer?.name,
      retailerToken: initialRetailer?.token,
    }))
  );

  return (
    <RetailerCoreContext.Provider value={store}>
      <RetailerProviders>{children}</RetailerProviders>
    </RetailerCoreContext.Provider>
  );
};

export const useRetailerCore = () => {
  const context = useContext(RetailerCoreContext);
  if (!context) {
    throw new Error(
      "RetailerCoreContext not found. Must be used within RetailerCoreProvider"
    );
  }
  return useStore(context);
};

export const useRetailerCoreWithSelector = <T,>(
  selector: (state: RetailerCore) => T
): T => {
  const context = useContext(RetailerCoreContext);
  if (!context) {
    throw new Error(
      "RetailerCoreContext not found. Must be used within RetailerCoreProvider"
    );
  }
  return useStore(context, selector);
};
